/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/comp_projects-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-comp_projects-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/comp_projects-entity-config.ts
 * - Run: pnpm run generate:api-clients (regenerates this file)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, subject_property_id, comp_type_id, review_status_id, created_by_user_profile_id, updated_by_user_profile_id, request_status_id, assigned_user_profile_id, assigned_by_user_profile_id, archived_by_user_profile_id, archive_batch_id
 * - Sortable fields: created_at, updated_at
 * - Required fields: id
 * - Readonly fields: id, created_at, updated_at, archived_at, archived_by_user_profile_id, archive_reason, archive_batch_id
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/comp_projects-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-comp_projects-api-client.ts (API client)
 * - client/api/generated_schemas/comp_projects.ts (TypeBox schemas and types)
 *
 * Source config: config/entities/comp_projects-entity-config.ts
 * Public fields: 26/26 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CompProjectsClient, CompProjectsQueryOptions } from '../generated_clients/generated-comp_projects-api-client.js';
import {
  CompProjectsInsert,
  CompProjectsUpdate,
  CompProjectsFieldConfig
} from '../../types/generated_schemas/generated-comp_projects-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/comp_projects_business_hooks/

// Query keys with enhanced caching strategy. A list key carries every
// parameter getAll sends, so two different pages never share a cache entry.
export const compProjectsKeys = {
  all: ['comp_projects'] as const,
  lists: () => [...compProjectsKeys.all, 'list'] as const,
  list: (filters: Record<string, any>, page?: Pick<CompProjectsQueryOptions, 'limit' | 'offset' | 'sortBy' | 'sortOrder'>) =>
    [...compProjectsKeys.lists(), { filters, ...page }] as const,
  details: () => [...compProjectsKeys.all, 'detail'] as const,
  detail: (id: string) => [...compProjectsKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...compProjectsKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...compProjectsKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...compProjectsKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...compProjectsKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useCompProjectsClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new CompProjectsClient();
};

/**
 * Hook to get all comp_projects with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with comp_projects data
 */
export const useCompProjectsList = (options?: CompProjectsQueryOptions) => {
  const client = useCompProjectsClient();
  
  return useQuery({
    queryKey: compProjectsKeys.list(options?.filters || {}, {
      limit: options?.limit,
      offset: options?.offset,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
    }),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single comp_project by ID
 *
 * @param id - The comp_project ID
 * @returns React Query result with single comp_project data
 */
export const useCompProjects = (id: string) => {
  const client = useCompProjectsClient();
  
  return useQuery({
    queryKey: compProjectsKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/comp_projects
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/comp_projects_business_hooks/
 */

/**
 * Hook to create a new comp_project
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at, archived_at, archived_by_user_profile_id, archive_reason, archive_batch_id
 *
 * @returns React Query mutation for creating comp_project
 */
export const useCreateCompProjects = () => {
  const client = useCompProjectsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CompProjectsInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: compProjectsKeys.all });
    },
  });
};

/**
 * Hook to update an existing comp_project
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at, archived_at, archived_by_user_profile_id, archive_reason, archive_batch_id
 *
 * @returns React Query mutation for updating comp_project
 */
export const useUpdateCompProjects = () => {
  const client = useCompProjectsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CompProjectsUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: compProjectsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: compProjectsKeys.lists() });
    },
  });
};

/**
 * Hook to delete a comp_project
 *
 * @returns React Query mutation for deleting comp_project
 */
export const useDeleteCompProjects = () => {
  const client = useCompProjectsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: compProjectsKeys.all });
    },
  });
};

/**
 * Hook to count comp_projects with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, subject_property_id, comp_type_id, review_status_id, created_by_user_profile_id, updated_by_user_profile_id, request_status_id, assigned_user_profile_id, assigned_by_user_profile_id, archived_by_user_profile_id, archive_batch_id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useCompProjectsCount = (filter?: Partial<Record<typeof CompProjectsFieldConfig.filterable[number], any>>) => {
  const client = useCompProjectsClient();
  
  return useQuery({
    queryKey: compProjectsKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useCompProjectsFieldConfig = () => {
  return CompProjectsFieldConfig;
};